import { Fragment, useLayoutEffect, useState } from "react";
import { cx } from "emotion";
import styles from "./Breadcrumb.module.css";
import type {
  BreadcrumbProps,
  BreadcrumbLink as TBreadcrumbLink
} from "./Breadcrumb.types";
import { BreadcrumbCurrent } from "./BreadcrumbCurrent/BreadcrumbCurrent";
import { BreadcrumbDropdown } from "./BreadcrumbDropdown/BreadcrumbDropdown";
import { BreadcrumbLink } from "./BreadcrumbLink/BreadcrumbLink";
import { BreadcrumbSeparator } from "./BreadcrumbSeparator/BreadcrumbSeparator";
import { BreadcrumbSiblingsDropdown } from "./BreadcrumbSiblingsDropdown/BreadcrumbSiblingsDropdown";
import { BreadcrumbGoToDropdown } from "./BreadcrumbGoToDropdown/BreadcrumbGoToDropdown";

/**
 * Breadcrumb Component
 *
 * A flexible breadcrumb navigation component that supports configurable link visibility
 * and responsive design with dropdown for overflow links.
 *
 * Architecture and Responsiveness:
 *
 * 1. Component Structure:
 *    - The Breadcrumb component is composed of several sub-components:
 *      BreadcrumbLink, BreadcrumbDropdown, BreadcrumbCurrent, and BreadcrumbSeparator.
 *    - This modular approach allows for easy customization and maintenance of each part.
 *
 * 2. Link Display Strategy:
 *    - Configurable number of links before dropdown (default: 1, can be 0)
 *    - Configurable number of links after dropdown (minimum: 1, default: 1)
 *    - Last link is always rendered as current (non-clickable)
 *    - Middle links are collapsed into a dropdown to save space and improve readability.
 *
 * 3. Configuration Options:
 *    - visibleLinksBeforeDropdown: Controls how many links appear before the dropdown
 *      * Default: 1 (shows first link)
 *      * Can be set to 0 to hide all leading links
 *      * Negative values are treated as 0
 *    - visibleLinksAfterDropdown: Controls how many links appear after the dropdown
 *      * Default: 1 (shows last link as current)
 *      * Minimum enforced: 1 (at least one link must be visible at the end)
 *      * Values less than 1 are automatically set to 1
 *
 * 4. Responsive Behavior:
 *    - When total links fit within visible limits, no dropdown is shown
 *    - Dropdown only appears when there are links to hide
 *    - Layout recalculates when props or links change
 *
 * 5. Sibling Links Feature:
 *    - siblingLinks: Optional array of links for the last breadcrumb item
 *    - If provided with links (length > 0), shows a dropdown with current page and siblings
 *    - If not provided or empty, shows last item as non-clickable current page
 *    - Useful for showing alternative paths or related pages at the current level
 *
 * 6. Go-To Links Feature:
 *    - goToLinks: Optional array of child/next page links that appear as a separate breadcrumb item
 *    - goToText: Text to display in the go-to trigger
 *    - Renders as a separate breadcrumb item after the current page
 *    - Useful for showing next steps or child pages from the current location
 *
 * @example
 * // Default behavior (1 before, 1 after)
 * <Breadcrumb links={links} />
 *
 * @example
 * // Show 2 links before dropdown, 2 after
 * <Breadcrumb
 *   links={links}
 *   visibleLinksBeforeDropdown={2}
 *   visibleLinksAfterDropdown={2}
 * />
 *
 * @example
 * // Minimal layout (0 before, 1 after)
 * <Breadcrumb
 *   links={links}
 *   visibleLinksBeforeDropdown={0}
 *   visibleLinksAfterDropdown={1}
 * />
 *
 * @example
 * // With sibling links for the last item
 * <Breadcrumb
 *   links={links}
 *   siblingLinks={[
 *     { label: "Overview", url: "/product/overview" },
 *     { label: "Reviews", url: "/product/reviews" },
 *     { label: "Specifications", url: "/product/specs" }
 *   ]}
 * />
 *
 * @example
 * // With go-to links as separate item
 * <Breadcrumb
 *   links={links}
 *   goToText="Go to workflow"
 *   goToLinks={[
 *     { label: "Design workflow", url: "/current/workflow/design" },
 *     { label: "Test workflow", url: "/current/workflow/test" },
 *     { label: "Deploy workflow", url: "/current/workflow/deploy" }
 *   ]}
 * />
 */

const Breadcrumb = ({
  links,
  rightNode,
  onLinkClick,
  dangerouslySetClassNames,
  visibleLinksBeforeDropdown = 1,
  visibleLinksAfterDropdown = 1,
  maxWidth,
  siblingLinks,
  goToLinks,
  goToText
}: BreadcrumbProps) => {
  const [dropdownLinks, setDropdownLinks] = useState<TBreadcrumbLink[]>([]);
  const [visibleBeforeLinks, setVisibleBeforeLinks] = useState<
    TBreadcrumbLink[]
  >([]);
  const [visibleAfterLinks, setVisibleAfterLinks] = useState<TBreadcrumbLink[]>(
    []
  );
  const shouldShowDropdown = dropdownLinks.length > 0;

  useLayoutEffect(() => {
    const calculateBreadcrumbLayout = () => {
      // Ensure minimum values
      const beforeCount = Math.max(0, visibleLinksBeforeDropdown);
      const afterCount = Math.max(1, visibleLinksAfterDropdown);

      if (links.length === 0) {
        setVisibleBeforeLinks([]);
        setDropdownLinks([]);
        setVisibleAfterLinks([]);
        return;
      }

      // If total links fit within visible limits, show all without dropdown
      if (links.length <= beforeCount + afterCount) {
        setVisibleBeforeLinks(links.slice(0, -1));
        setDropdownLinks([]);
        setVisibleAfterLinks(links.slice(-1));
        return;
      }

      // Calculate visible links before dropdown
      const beforeLinks = links.slice(0, beforeCount);

      // Calculate visible links after dropdown (from the end)
      const afterLinks = links.slice(-afterCount);

      // Calculate dropdown links (everything in between)
      const dropdownStartIndex = beforeCount;
      const dropdownEndIndex = links.length - afterCount;
      const middleLinks = links.slice(dropdownStartIndex, dropdownEndIndex);

      setVisibleBeforeLinks(beforeLinks);
      setDropdownLinks(middleLinks);
      setVisibleAfterLinks(afterLinks);
    };

    requestAnimationFrame(calculateBreadcrumbLayout);
  }, [links, visibleLinksBeforeDropdown, visibleLinksAfterDropdown]);

  return (
    <ol
      className={cx(dangerouslySetClassNames?.container, [
        styles.breadcrumbLinkList
      ])}
    >
      {/* Render visible links before dropdown */}
      {visibleBeforeLinks.map((link, index) => (
        <Fragment key={`before-${index}`}>
          <BreadcrumbLink
            link={link}
            onLinkClick={onLinkClick}
            maxWidth={maxWidth}
          />
          <BreadcrumbSeparator />
        </Fragment>
      ))}

      {/* Render dropdown if there are hidden links */}
      {shouldShowDropdown && (
        <Fragment>
          <BreadcrumbDropdown links={dropdownLinks} onLinkClick={onLinkClick} />
          <BreadcrumbSeparator />
        </Fragment>
      )}

      {/* Render visible links after dropdown */}
      {visibleAfterLinks.map((link, index) => {
        const isLast = index === visibleAfterLinks.length - 1;
        return (
          <Fragment key={`after-${index}`}>
            {!isLast ? (
              <BreadcrumbLink
                link={link}
                onLinkClick={onLinkClick}
                maxWidth={maxWidth}
              />
            ) : siblingLinks && siblingLinks.length > 0 ? (
              <BreadcrumbSiblingsDropdown
                currentLink={link}
                siblingLinks={siblingLinks}
                onLinkClick={onLinkClick}
              />
            ) : (
              <BreadcrumbCurrent link={link} maxWidth={maxWidth} />
            )}
            {!isLast && <BreadcrumbSeparator />}
          </Fragment>
        );
      })}

      {/* Render separate go-to item if provided */}
      {goToLinks && goToLinks.length > 0 && goToText && (
        <Fragment>
          <BreadcrumbSeparator />
          <BreadcrumbGoToDropdown
            goToText={goToText}
            goToLinks={goToLinks}
            onLinkClick={onLinkClick}
          />
        </Fragment>
      )}

      {rightNode}
    </ol>
  );
};

export { Breadcrumb, BreadcrumbDropdown, BreadcrumbLink, BreadcrumbSeparator };
